import styled from '@emotion/styled';
import { Theme } from '@seedlang/constants';
import { AppUI } from '@seedlang/state';
import ReviewCardActions from 'components/review/review_card_actions';
import ReviewInfoTable from 'components/review/review_info_table';
import React from 'react';
import ReactTooltip from 'react-tooltip';
import { isPresent } from '@seedlang/utils';
import Checkbox from 'components/checkbox';

const Wrapper = styled.div`
  display: flex;
  background: ${props => props.embedded ? 'transparent' : '#f1f1f1'};
  padding: ${props => props.embedded ? '0' : '10px'};
  margin: ${props => props.embedded ? '0' : '10px 0'};
  flex-direction: column;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const CardHeaderLeft = styled.div`
  display: flex;
  align-items: center;
`;

const ExpandCaret = styled.div`
  display: flex;
  align-items: center;
  color: #7d7c7c;
  font-size: 12px;
  cursor: pointer;
  width: 10px;
  margin: 0 2px 0 -2px;
`;

const CardSummary = styled.div`
  width: 100%;
  display: flex;
  gap: 0 20px;
  align-items: left;
  justify-content: space-between;
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
`;

const ExpandedCardContent = styled.div`
  margin-top: ${props => props.marginTop || '10px'};
`;

const SummaryText = styled.div`
  color: ${props => props.color || '#333'};
`;

const IconsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  > div {
    display: flex;
    align-items: center;
  }
`;

const NewLabel = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${Theme.blue};
  color: #FFF;
  border-radius: 8px;
  font-size: 10px;
  font-weight: bold;
  padding: 0 8px;
`;

const ReviewTag = styled.div`
  background: ${props => props.background};
  color: #FFF;
  border-radius: 8px;
  font-size: 12px;
  padding: 0 10px;
  font-weight: bold;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

const cardIsNew = (card) => {
  return Boolean(card?.createdAt?.dateObj && (Date.now() - card.createdAt.dateObj.getTime()) < (60 * 60 * 1000));
};

const cardDeckIsArchived = (card) => {
  if (!card) {
    return false;
  }
  return AppUI.reviewDeckStore.indexData.find(item => item.id === card.deckId)?.archived;
};

const reviewIsPastDue = (reviewAfter) => {
  const reviewAfterDate = reviewAfter?.dateObj || reviewAfter?.date;
  if (!reviewAfterDate) {
    return false;
  }
  const now = new Date();
  const reviewsDueAtBeginningOfDay = AppUI.user?.reviewsDueAtBeginningOfDay !== false;
  const cutoff = reviewsDueAtBeginningOfDay ? new Date(now.setHours(23, 59, 59, 999)) : new Date();
  return new Date(reviewAfterDate) < cutoff;
};

const reviewAfterText = (reviewAfter) => {
  if (!reviewAfter) {
    return '-';
  }
  const reviewsDueAtBeginningOfDay = AppUI.user?.reviewsDueAtBeginningOfDay !== false;
  if (reviewsDueAtBeginningOfDay) {
    return `${reviewAfter.fullTextFormattedDateWithYear}${reviewAfter.relativeDays ? ` (${reviewAfter.relativeDays})` : ''}`;
  }
  return `${reviewAfter.formattedDateWithYearAndTime || '-'}${reviewAfter.relativeTime ? ` (${reviewAfter.relativeTime})` : ''}`;
};

const cardTypeName = (card) => {
  if (!card) {
    return '-';
  }
  if (card.cardType?.name) {
    return card.cardType.name;
  }
  const cardType = AppUI.cardTypeStore.indexData.find(item => item.id === card.cardTypeId);
  return cardType?.name || card.cardTypeName || '-';
};

const reviewDeckName = (card, reviewDeckOptions) => {
  if (!card) {
    return '-';
  }
  const option = (reviewDeckOptions || []).find(item => item[0] === card.deckId);
  if (option) {
    return option[1];
  }
  return AppUI.reviewDeckStore.indexData.find(item => item.id === card.deckId)?.name || '-';
};

const frontText = (card) => {
  if (!card) {
    return '';
  }
  const maxLength = (AppUI.layout.isMobile || AppUI.layout.isNative) ? 50 : 100;
  if (typeof card.reviewFrontText === 'function') {
    return card.reviewFrontText(maxLength);
  }
  const text = card.frontText;
  if (text && text.length > maxLength) {
    return `${text.substring(0, maxLength)}...`;
  }
  return text || '';
};

const wordBackText = (card) => {
  if (!card?.word) {
    return '';
  }
  const user = AppUI.user;
  if (user && typeof card.word.sourceTextForUser === 'function') {
    return card.word.sourceTextForUser(user);
  }
  const sourceListForUser = user && typeof card.word.vocabSourceListForUser === 'function'
    ? card.word.vocabSourceListForUser(user, { forceHideEnglishTranslations: true })
    : null;
  if (sourceListForUser && sourceListForUser.length) {
    return sourceListForUser.join(', ');
  }
  return card.word.vocabSourceList || '';
};

const backText = (card) => {
  if (!card) {
    return '';
  }
  if (card.cardTypeSlug === 'exercise') {
    return '';
  }
  if (card.userSubmission && card.cardTypeSlug === 'user_submission') {
    return card.backText || '';
  }
  if (card.word && !card.sentence) {
    return wordBackText(card);
  }
  if (card.sentence) {
    const sourceTextForUser = AppUI.user && typeof card.sentence.sourceTextForUser === 'function'
      ? card.sentence.sourceTextForUser(AppUI.user)
      : null;
    if (sourceTextForUser) {
      return sourceTextForUser;
    }
    return card.sentence.sourceText || '';
  }
  return card.backText || '';
};

const reviewStatus = (card) => {
  if (card?.status) {
    return card.status;
  }
  if (card?.retired) {
    return 'Retired';
  }
  return reviewIsPastDue(card?.reviewAfter) ? 'Past Due' : 'Caught Up';
};

const ReviewCardPanel = ({
  item,
  expanded = false,
  forceExpanded = false,
  onToggleExpanded,
  showCheckbox = true,
  checkboxValue,
  onToggleCheckbox,
  reviewDeckOptions,
  onManage,
  onReset,
  onDelete,
  embedded = false,
  contentMarginTop,
  actionsMarginTop,
  infoTableMarginBottom,
}) => {
  const isExpanded = forceExpanded || expanded;
  const showExpandCaret = Boolean(!forceExpanded && onToggleExpanded);
  const status = reviewStatus(item);
  const archived = cardDeckIsArchived(item);
  const resolvedDeckName = item?.deckName || reviewDeckName(item, reviewDeckOptions);
  const showArchivedDeckTag = archived && resolvedDeckName !== '-';
  const deckNameWithArchivedTag = showArchivedDeckTag ? `${resolvedDeckName} [archived]` : resolvedDeckName;
  const isPastDue = !item?.retired && reviewIsPastDue(item?.reviewAfter);
  const isCaughtUp = !item?.retired && !isPastDue;
  const dueText = item?.dueText || reviewAfterText(item?.reviewAfter);
  const resolvedDueText = item?.retired ? null : dueText;
  const resolvedBackText = backText(item);
  const rows = [
    isPresent(resolvedBackText) ? {
      label: 'Back',
      value: resolvedBackText,
    } : null,
    {
      label: 'Deck',
      value: deckNameWithArchivedTag,
    },
    {
      label: 'Card Type',
      value: item?.cardTypeName || cardTypeName(item),
    },
    item?.createdAt ? {
      label: 'Created',
      value: item.createdAt?.fullTextFormattedDateWithYear || '-',
    } : null,
    resolvedDueText ? {
      label: 'Due',
      value: resolvedDueText,
    } : null,
    item?.difficultyLabel ? {
      label: 'Difficulty',
      value: (
        <ReviewTag background={item.difficultyColor}>
          {item.difficultyLabel}
        </ReviewTag>
      ),
    } : null,
    {
      label: 'Status',
      value: (
        <ReviewTag
          background={status === 'Retired' ? Theme.purple : status === 'Past Due' ? Theme.orange : Theme.green}
        >
          {
            status === 'Retired' && !archived &&
            <i className='fa fa-archive' style={{fontSize: '11px'}} />
          }
          {
            status === 'Past Due' && !archived &&
            <i className='fa fa-clock-o' style={{fontSize: '11px'}} />
          }
          {
            status === 'Caught Up' && !archived &&
            <i className='fa fa-check' style={{fontSize: '11px'}} />
          }
          {status}
        </ReviewTag>
      ),
    },
  ];

  return (
    <Wrapper
      embedded={embedded}
    >
      <CardHeader>
        <CardHeaderLeft>
          {
            showExpandCaret &&
            <ExpandCaret
              onClick={e => {
                e.stopPropagation();
                onToggleExpanded();
              }}
            >
              <i className={isExpanded ? 'fa fa-angle-down' : 'fa fa-angle-right'} />
            </ExpandCaret>
          }
          {
            showCheckbox &&
            <Checkbox
              margin='0 10px 0 0'
              value={checkboxValue}
              onClick={onToggleCheckbox}
            />
          }
        </CardHeaderLeft>
        <CardSummary
          clickable={showExpandCaret}
          onClick={showExpandCaret ? onToggleExpanded : null}
        >
          <SummaryText color={archived || item?.retired ? '#636363' : null}>
            {frontText(item)}
          </SummaryText>
          <IconsWrapper>
            {
              cardIsNew(item) &&
              <NewLabel>
                NEW
              </NewLabel>
            }
            {
              archived &&
              <div
                data-tip
                data-for={`${item.id}-archived-deck`}
              >
                <i
                  className='fa fa-archive'
                  style={{ color: '#636363', fontSize: '15px' }}
                />
                <ReactTooltip
                  type='dark'
                  effect='solid'
                  id={`${item.id}-archived-deck`}
                  class='custom-tooltip'
                >
                  Archived
                </ReactTooltip>
              </div>
            }
            {
              item?.retired && !archived &&
              <div
                data-tip
                data-for={`${item.id}-retired`}
              >
                <i
                  className='fa fa-archive'
                  style={{ color: '#636363', fontSize: '15px' }}
                />
                <ReactTooltip
                  type='dark'
                  effect='solid'
                  id={`${item.id}-retired`}
                  class='custom-tooltip'
                >
                  Retired
                </ReactTooltip>
              </div>
            }
            {
              isPastDue && !archived &&
              <div
                data-tip
                data-for={`${item.id}-due-status`}
              >
                <i
                  className='fa fa-clock-o'
                  style={{ color: Theme.orange, fontSize: '16px' }}
                />
                <ReactTooltip
                  type='dark'
                  effect='solid'
                  id={`${item.id}-due-status`}
                  class='custom-tooltip'
                >
                  Past Due
                </ReactTooltip>
              </div>
            }
            {
              isCaughtUp && !archived &&
              <div
                data-tip
                data-for={`${item.id}-due-caught-up`}
              >
                <i
                  className='fa fa-check'
                  style={{ color: Theme.green, fontSize: '14px' }}
                />
                <ReactTooltip
                  type='dark'
                  effect='solid'
                  id={`${item.id}-due-caught-up`}
                  class='custom-tooltip'
                >
                  Caught Up
                </ReactTooltip>
              </div>
            }
          </IconsWrapper>
        </CardSummary>
      </CardHeader>
      {
        isExpanded &&
        <ExpandedCardContent marginTop={contentMarginTop || (embedded ? '0' : '10px')}>
          <ReviewInfoTable
            rows={rows}
            marginBottom={infoTableMarginBottom || (embedded ? '6px' : '10px')}
          />
          {
            (onManage || onReset || onDelete) &&
            <ReviewCardActions
              marginTop={actionsMarginTop || (embedded ? '6px' : '10px')}
              onManage={onManage}
              onReset={onReset}
              onDelete={onDelete}
            />
          }
        </ExpandedCardContent>
      }
    </Wrapper>
  );
};

export default ReviewCardPanel;
